/**
 * The page shell, at both levels of the site.
 *
 * Site-level pages (`/`, the account pages, and later the pages about the
 * site) have the brand, the language toggle and the account link, and no
 * world. A world's pages add the world's name — a label while there is one
 * world, a control when there are more — and that world's navigation, every
 * link under the world's own prefix.
 *
 * The footer notice is not optional: the ip-safety skill requires it on every
 * page, in both languages. It is one sentence; on a world's pages the world's
 * own clause, from the world's row, takes the place of the general one, so a
 * page about another world never names this one's author or estate.
 *
 * The header keeps the account link at its right end, beside the language
 * toggle, so the account menu planned for later can replace the link without
 * moving anything else (decision 007).
 */

import type { ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router'
import { signInPath } from '../../shared/return-path.ts'
import { couldBeWorld, worldPath } from '../../shared/routes.ts'
import { quizFor } from '../../shared/quizzes/index.ts'
import { siteNotice } from '../../shared/site-notice.ts'
import { bg } from '../i18n/bg.ts'
import { en } from '../i18n/en.ts'
import { useI18n } from '../i18n/index.tsx'
import { authClient } from '../lib/auth-client.ts'
import { useWorlds, worldName } from '../lib/catalogue.ts'
import { ShelfProvider } from '../lib/shelf.tsx'
import { useCurrentWorld, WorldProvider } from '../lib/world.tsx'
import { CompassMark, Knot } from './Ornament.tsx'

/** Site pages. `threshold` is the front page, which sits on ink: a threshold, not a reading surface. */
export function SiteLayout({ threshold = false }: { threshold?: boolean }) {
  return (
    <Shell threshold={threshold}>
      <Outlet />
    </Shell>
  )
}

export function WorldLayout() {
  const { world = '' } = useParams()
  // `/contact` and the other reserved segments are site pages, not worlds; one
  // that is not built yet is simply not there.
  if (!couldBeWorld(world)) {
    return (
      <Shell>
        <PageNotFound />
      </Shell>
    )
  }
  return (
    // Keyed by the world, so moving to another world starts from a clean
    // loading state rather than showing the last world's catalogue.
    <WorldProvider key={world} slug={world}>
      <ShelfProvider world={world}>
        <Shell>
          <Outlet />
        </Shell>
      </ShelfProvider>
    </WorldProvider>
  )
}

function PageNotFound() {
  const { t } = useI18n()
  return (
    <section className="panel">
      <h1>{t('page.not_found.title')}</h1>
      <p>
        {t('page.not_found.body')} <Link to="/">{t('world.all')}</Link>
      </p>
    </section>
  )
}

function Shell({ children, threshold = false }: { children: ReactNode; threshold?: boolean }) {
  const { t, locale, setLocale } = useI18n()
  const { data: session } = authClient.useSession()
  const location = useLocation()
  const world = useCurrentWorld()
  const universe = world !== null && (world.state.status === 'loaded' || world.state.status === 'not-loaded')
    ? world.state.universe
    : null

  return (
    <div className="shell">
      <header className="masthead">
        <div className="masthead__inner">
          {/* The brand is the site, not the world: it always goes to the worlds. */}
          <Link to="/" className="masthead__brand">
            <CompassMark />
            {t('app.name')}
          </Link>
          {world !== null && universe !== null ? (
            <>
              <WorldControl current={world.slug} currentName={worldName(universe, locale)} />
              <nav aria-label={t('nav.label')} className="masthead__nav">
                <NavLink to={worldPath(world.slug)} end>
                  {t('nav.home')}
                </NavLink>
                {/* Second, as the navigation was specified, and only where the world has a quiz. */}
                {quizFor(world.slug) === null ? null : (
                  <NavLink to={worldPath(world.slug, 'quiz')} end>
                    {t('nav.quiz')}
                  </NavLink>
                )}
                <NavLink to={worldPath(world.slug, 'series')} end>
                  {t('nav.series')}
                </NavLink>
                <NavLink to={worldPath(world.slug, 'books')} end>
                  {t('nav.books')}
                </NavLink>
              </nav>
            </>
          ) : null}
          <div className="masthead__tools">
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                setLocale(locale === 'bg' ? 'en' : 'bg')
              }}
            >
              {t('app.footer.language')}
            </button>
            {session ? (
              // Signed in, and says so: a mark and the reader's nickname, or
              // „Профил“ without one. Never the email address — the header is
              // on every page, and a screen is read over shoulders.
              <Link to="/account" className="masthead__account">
                <span className="masthead__account-mark" aria-hidden="true" />
                <span className="visually-hidden">{t('header.signed_in')} </span>
                <span className="masthead__account-name">{session.user.name.trim() === '' ? t('account.title') : session.user.name.trim()}</span>
              </Link>
            ) : (
              // Back to this page after signing in: shared/return-path.ts.
              <Link to={signInPath(location.pathname)}>{t('signin.title')}</Link>
            )}
          </div>
        </div>
      </header>

      <main className={threshold ? 'shell__main shell__main--threshold' : 'shell__main'}>{children}</main>

      <footer className="shell__footer">
        <Knot />
        <nav className="shell__footer-nav" aria-label={t('footer.nav')}>
          <Link to="/contact">{t('contact.title')}</Link>
          <Link to="/terms">{t('terms.title')}</Link>
          <Link to="/privacy">{t('privacy.title')}</Link>
          <Link to="/about">{t('about.title')}</Link>
        </nav>
        {/* Both languages, always — this is a legal notice, not a string that
            happens to need translating. On a world's pages the world's own
            clause replaces the general one in the middle of the sentence. */}
        <p lang="bg">{universe === null ? bg['app.footer.disclaimer'] : siteNotice('bg', universe.disclaimer_bg)}</p>
        <p lang="en">{universe === null ? en['app.footer.disclaimer'] : siteNotice('en', universe.disclaimer_en)}</p>
      </footer>
    </div>
  )
}

/**
 * Which world the reader is in. With one world there is nothing to choose, so
 * it is a label; with more it is a menu. Built as the control from the start,
 * so a second world needs no change here.
 */
function WorldControl({ current, currentName }: { current: string; currentName: string }) {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const worlds = useWorlds()

  if (worlds.status !== 'ready' || worlds.worlds.length < 2) {
    return <span className="masthead__world">{currentName}</span>
  }
  return (
    <select
      aria-label={t('nav.world')}
      className="masthead__world"
      value={current}
      onChange={(event) => {
        void navigate(worldPath(event.target.value))
      }}
    >
      {worlds.worlds.map((w) => (
        <option key={w.slug} value={w.slug}>
          {worldName(w, locale)}
        </option>
      ))}
    </select>
  )
}
